import { getConfig } from '@/config/runtime'

import { PRODIGY_FILE_ENDPOINTS } from './endpoints'
import {
  DownloadFileResponse,
  DownloadProjectFileOptions,
  ProjectFileUploadMetadata,
  UploadFileRequest,
  UploadFileResponse,
  UploadProjectFileOptions,
  UploadThumbnailRequest,
  UploadThumbnailResponse,
} from './types'

const joinUrl = (baseUrl: string, path: string): string =>
  `${baseUrl.replace(/\/+$/, '')}${path.startsWith('/') ? path : `/${path}`}`

const resolveToken = async (options: UploadProjectFileOptions): Promise<string | undefined> => {
  if (options.token) return options.token
  if (options.getToken) return options.getToken()
  return undefined
}

const assertUploadFileResponse = (response: UploadFileResponse): UploadFileResponse => {
  if (!response?.uploadUrl || !response?.key) {
    throw new Error('Upload file response is missing uploadUrl or key')
  }

  return response
}

const requestUploadFile = async (
  payload: UploadFileRequest,
  options: UploadProjectFileOptions = {}
): Promise<UploadFileResponse> => {
  const token = await resolveToken(options)
  const response = await fetch(
    joinUrl(getConfig().pag.awsApiUrl, PRODIGY_FILE_ENDPOINTS.uploadFile),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal: options.signal,
    }
  )

  if (!response.ok) {
    throw new Error(`Unable to prepare file upload (${response.status})`)
  }

  return assertUploadFileResponse(await response.json())
}

const uploadToPresignedUrl = (
  file: File,
  uploadTarget: UploadFileResponse,
  options: UploadProjectFileOptions = {}
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const abortUpload = () => {
      xhr.abort()
      reject(new Error('File upload was cancelled'))
    }

    if (options.signal?.aborted) {
      abortUpload()
      return
    }

    options.signal?.addEventListener('abort', abortUpload, { once: true })

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return
      options.onProgress?.(Math.round((event.loaded * 100) / event.total))
    }

    xhr.onload = () => {
      options.signal?.removeEventListener('abort', abortUpload)
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
        return
      }
      reject(new Error(`File upload failed (${xhr.status})`))
    }

    xhr.onerror = () => {
      options.signal?.removeEventListener('abort', abortUpload)
      reject(new Error('Network error during file upload'))
    }

    xhr.open('PUT', uploadTarget.uploadUrl)

    const headers = uploadTarget.headers || {}
    Object.keys(headers).forEach((key) => {
      xhr.setRequestHeader(key, headers[key])
    })

    if (!headers['Content-Type'] && !headers['content-type']) {
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    }

    xhr.send(file)
  })

const toProjectFileMetadata = (
  file: File,
  uploadTarget: UploadFileResponse
): ProjectFileUploadMetadata => ({
  key: uploadTarget.key,
  filename: file.name,
  source: 'local_file_system',
  url: uploadTarget.url || uploadTarget.key,
  mimetype: file.type || 'application/octet-stream',
  size: file.size,
})

export const uploadProjectFile = async (
  file: File,
  options: UploadProjectFileOptions = {}
): Promise<ProjectFileUploadMetadata> => {
  if (!file) {
    throw new Error('No file selected')
  }

  const uploadTarget = await requestUploadFile(
    {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      tenantId: options.tenantId !== undefined ? String(options.tenantId) : undefined,
      projectId: options.projectId !== undefined ? String(options.projectId) : undefined,
      uploadedBy: options.uploadedBy,
      projectNumber: options.projectNumber,
      brandId: options.brandId !== undefined ? String(options.brandId) : undefined,
      agencyId: options.agencyId !== undefined ? String(options.agencyId) : undefined,
    },
    options
  )

  await uploadToPresignedUrl(file, uploadTarget, options)

  let downloadUrl = uploadTarget.url || uploadTarget.key
  try {
    const downloadTarget = await downloadProjectFile(uploadTarget.key, options)
    if (downloadTarget?.downloadUrl) {
      downloadUrl = downloadTarget.downloadUrl
    }
  } catch (error) {
    console.error('Failed to get download URL in uploadProjectFile:', error)
  }

  const metadata = toProjectFileMetadata(file, uploadTarget)
  metadata.url = downloadUrl
  return metadata
}


export const downloadProjectFile = async (
  key: string,
  options: DownloadProjectFileOptions = {}
): Promise<DownloadFileResponse> => {
  const token = await resolveToken(options)
  const response = await fetch(
    joinUrl(getConfig().pag.awsApiUrl, PRODIGY_FILE_ENDPOINTS.downloadFile),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ key }),
    }
  )

  if (!response.ok) {
    throw new Error(`Unable to get download URL (${response.status})`)
  }

  return response.json()
}

const requestUploadThumbnail = async (
  payload: UploadThumbnailRequest,
  options: UploadProjectFileOptions = {}
): Promise<UploadThumbnailResponse> => {
  const token = await resolveToken(options)
  const response = await fetch(
    joinUrl(getConfig().pag.awsApiUrl, PRODIGY_FILE_ENDPOINTS.uploadThumbnail),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal: options.signal,
    }
  )

  if (!response.ok) {
    throw new Error(`Unable to prepare thumbnail upload (${response.status})`)
  }

  const result = await response.json()
  if (!result?.uploadUrl || !result?.key) {
    throw new Error('Upload thumbnail response is missing uploadUrl or key')
  }

  return result
}

export const uploadProjectThumbnail = async (
  file: File,
  key: string,
  options: UploadProjectFileOptions = {}
): Promise<UploadThumbnailResponse> => {
  if (!file) {
    throw new Error('No file selected')
  }

  const uploadTarget = await requestUploadThumbnail(
    {
      key,
      contentType: file.type || 'image/jpeg',
    },
    options
  )

  await uploadToPresignedUrl(file, uploadTarget as any, options)

  return uploadTarget
}

export const ProdigyFileService = {
  requestUploadFile,
  uploadProjectFile,
  downloadProjectFile,
  uploadProjectThumbnail,
}
